import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  Settlement,
  SettlementRequest,
  SettlementRequestStatus,
  SettleRequestPayload,
} from "../../interfaces/OnboardingObject";
import { getRequest, postRequest } from "../http/httpRequest";
import { ApiEnvelopeError, coerceSingle, unwrapEnvelope } from "../helpers/ApiEnvelopeHelper";
import {
  convertSettlement,
  convertSettlementRequestList,
} from "../converters/backendToUi/onboardingConvertor";
import { buildSettleRequestPayload } from "../converters/uiToBackend/OnboardingConvertor";

// Admin -> Settlements (admin-general). Admin-only: admin-general checks
// profiles.is_admin, then forwards to the user service, which owns the data
// and re-checks admin. Errors arrive with real HTTP statuses + errorCode.
const BEARER = true;

const ACCESS_DENIED_CODES = new Set(["FORBIDDEN", "NOT_AUTHORIZED", "ACCESS_DENIED", "HTTP_403"]);

const FRIENDLY_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "Your session has expired. Please log in again.",
  HTTP_401: "Your session has expired. Please log in again.",
  FORBIDDEN: "You don't have access to settlements.",
  NOT_AUTHORIZED: "You don't have access to settlements.",
  ACCESS_DENIED: "You don't have access to settlements.",
  HTTP_403: "You don't have access to settlements.",
  VALIDATION_ERROR: "The payment details entered aren't valid. Check the reference and note.",
  HTTP_400: "The payment details entered aren't valid. Check the reference and note.",
  SETTLEMENT_REQUEST_NOT_FOUND: "This request no longer exists. The list has been refreshed.",
  SETTLEMENT_REQUEST_NOT_PENDING: "This request was already settled or cancelled. The list has been refreshed.",
  SETTLEMENT_AMOUNT_CHANGED: "The requested amount changed. Check the refreshed amount before paying.",
  SETTLEMENT_EXCEEDS_UNSETTLED:
    "This request is more than the person's unsettled balance now. Ask them to cancel and request again.",
  SETTLEMENT_SERVICE_UNAVAILABLE: "Settlements are unavailable right now. Please try again.",
};

const OUTCOME_UNKNOWN_MESSAGE =
  "We couldn't confirm whether the payment was recorded. The list has been refreshed — check it before trying again.";

export function isAdminSettlementAccessDenied(error: unknown): boolean {
  return error instanceof ApiEnvelopeError && ACCESS_DENIED_CODES.has(error.errorCode);
}

/** Display string for a failed list call. */
export function toFriendlyAdminSettlementError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    return FRIENDLY_MESSAGES[error.errorCode] ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}

/**
 * What a failed settle means — the ONE place that decides it.
 * - "rejected": the server refused before writing anything (auth, validation);
 *               the admin can fix the input and retry in the same dialog.
 * - "stale":    the request changed under us (already settled, amount changed,
 *               gone); nothing was written now, but the list must be reloaded.
 * - "unknown":  anything else — timeout, gateway error, network drop,
 *               SETTLEMENT_OUTCOME_UNKNOWN. The payment MAY have been recorded,
 *               so never invite a blind retry: reload and let the admin check.
 */
export type SettleOutcome = "rejected" | "stale" | "unknown";

const REJECTED_CODES = new Set([
  "VALIDATION_ERROR",
  "HTTP_400",
  "UNAUTHENTICATED",
  "HTTP_401",
  ...ACCESS_DENIED_CODES,
]);
const STALE_CODES = new Set([
  "SETTLEMENT_REQUEST_NOT_FOUND",
  "SETTLEMENT_REQUEST_NOT_PENDING",
  "SETTLEMENT_AMOUNT_CHANGED",
  "SETTLEMENT_EXCEEDS_UNSETTLED",
]);

export function classifySettleError(error: unknown): { outcome: SettleOutcome; message: string } {
  const code = error instanceof ApiEnvelopeError ? error.errorCode : "";
  if (REJECTED_CODES.has(code) || STALE_CODES.has(code)) {
    return {
      outcome: REJECTED_CODES.has(code) ? "rejected" : "stale",
      message: FRIENDLY_MESSAGES[code] ?? (error as Error).message,
    };
  }
  return { outcome: "unknown", message: OUTCOME_UNKNOWN_MESSAGE };
}

/** PENDING oldest first (pay in order); other statuses newest first. Max 200. */
async function listSettlementRequests(
  status: SettlementRequestStatus
): Promise<SettlementRequest[]> {
  const response = await getRequest(
    `${ServiceEndpoints.ADMIN_SETTLEMENT_REQUESTS}?status=${encodeURIComponent(status)}`,
    BEARER
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return convertSettlementRequestList(contents);
}

/** Records that Nine paid this request in full; closes it. */
async function settleRequest(
  requestId: string,
  payload: SettleRequestPayload
): Promise<Settlement> {
  const endpoint = ServiceEndpoints.ADMIN_SETTLEMENT_SETTLE.replace(
    "{requestId}",
    encodeURIComponent(requestId)
  );
  const response = await postRequest(endpoint, buildSettleRequestPayload(payload), BEARER);
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertSettlement(contents);
}

const AdminSettlementApiService = {
  listSettlementRequests,
  settleRequest,
};

export default AdminSettlementApiService;
